import { useEffect, useRef, useState } from "react";
import { Camera, CameraOff, Loader2, ScanLine } from "lucide-react";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Mobile-first 1D/2D barcode camera. ZXing is used instead of the browser-only
 * BarcodeDetector API so the same flow also works in current iOS Safari.
 */
export function CameraBarcodeScanner({ open, onOpenChange, onDetected, dark = false }) {
  const { t } = useI18n();
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const lastResultRef = useRef({ value: "", at: 0 });
  const onDetectedRef = useRef(onDetected);
  const onOpenChangeRef = useRef(onOpenChange);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  onDetectedRef.current = onDetected;
  onOpenChangeRef.current = onOpenChange;

  useEffect(() => {
    if (!open) return undefined;
    let disposed = false;
    let reader;
    setState("starting");
    setError("");

    (async () => {
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        reader = new BrowserMultiFormatReader();
        const controls = await reader.decodeFromConstraints(
          {
            audio: false,
            video: {
              facingMode: { ideal: "environment" },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          },
          videoRef.current,
          (result) => {
            if (!result || disposed) return;
            const value = result.getText()?.trim();
            const now = Date.now();
            if (!value || (lastResultRef.current.value === value && now - lastResultRef.current.at < 1500)) return;
            lastResultRef.current = { value, at: now };
            navigator.vibrate?.(40);
            onDetectedRef.current(value);
            onOpenChangeRef.current(false);
          },
        );
        if (disposed) controls.stop();
        else {
          controlsRef.current = controls;
          setState("scanning");
        }
      } catch (err) {
        if (disposed) return;
        const denied = err?.name === "NotAllowedError" || err?.name === "PermissionDeniedError";
        setError(denied ? t("scan.camera_permission") : t("scan.camera_error"));
        setState("error");
      }
    })();

    return () => {
      disposed = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
    };
  }, [open, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={dark ? "border-slate-800 bg-slate-900 text-slate-100 sm:max-w-lg" : "sm:max-w-lg"}
        data-testid="camera-scanner-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Camera className="h-5 w-5" />{t("scan.camera_title")}</DialogTitle>
          <DialogDescription className={dark ? "text-slate-400" : ""}>{t("scan.camera_hint")}</DialogDescription>
        </DialogHeader>
        <div className="relative overflow-hidden rounded-xl bg-black">
          <video ref={videoRef} muted playsInline className="aspect-[4/3] w-full object-cover" data-testid="camera-scanner-video" />
          {state === "starting" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-white">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />{t("scan.camera_starting")}
            </div>
          )}
          {state === "scanning" && (
            <div className="pointer-events-none absolute inset-[18%] rounded-lg border-2 border-emerald-400 shadow-[0_0_0_999px_rgba(0,0,0,.28)]">
              <ScanLine className="absolute -right-3 -top-3 h-6 w-6 rounded-full bg-emerald-500 p-1 text-slate-950" />
            </div>
          )}
          {state === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/85 p-6 text-center text-white">
              <CameraOff className="mb-3 h-8 w-8 text-amber-400" />
              <p className="text-sm">{error}</p>
            </div>
          )}
        </div>
        <Button variant="outline" onClick={() => onOpenChange(false)} className={dark ? "border-slate-700 bg-slate-800" : ""}>
          {t("common.close")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
